import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Parallax } from "react-parallax";
import VideoBackground from "../../../Components/VideoBackground";
import { useWindowSize, getLinkUrl } from "../../../Helpers";

// Color mappings translated into semantic Tailwind utility classes
const tailwindColorOptions = {
  transparent1:
    "text-white bg-transparent border-white hover:text-black hover:bg-transparent hover:border-black",
  transparent2:
    "text-black bg-transparent border-black hover:text-white hover:bg-transparent hover:border-white",
  black:
    "text-white bg-black border-black hover:text-black hover:bg-white hover:border-white",
  white:
    "text-black bg-white border-white hover:text-white hover:bg-black hover:border-black",
  blue: "text-white bg-blue-600 border-blue-600 hover:text-white hover:bg-blue-700 hover:border-blue-700",
  red: "text-white bg-error border-bright-coral hover:text-white hover:bg-red-700 hover:border-red-700",
};

// Simple text color fallbacks and arbitrary nested strong element mappings
const textColors = {
  white: "[&_p]:text-white",
  black: "[&_p]:text-black",
  blue: "[&_p]:text-jay-blue",
  red: "[&_p]:text-red-600",
};

const bodyTextColors = {
  white: "text-white",
  black: "text-black",
  blue: "text-jay-blue",
  red: "text-red-600",
};

const boldHeadingColors = {
  white: "[&_strong]:text-white",
  black: "[&_strong]:text-black",
  blue: "[&_strong]:text-jay-blue",
  red: "[&_strong]:text-red-600",
};

export default function QipHero({ slides = [] }) {
  const navigate = useNavigate();
  const windowSize = useWindowSize();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);

  if (!slides || slides.length === 0) return null;

  const activeSlide = slides[currentSlide];
  const {
    align,
    media_type,
    image,
    video,
    heading_color,
    heading_richtext,
    bold_heading_rich_text_color, // Destructured property
    copy,
    copy_color,
    cta,
    cta_color,
    cta_alt,
    cta_color_alt,
  } = activeSlide;

  const imageUrl = image?.sizes?.hero_banner;
  const mediaURL = "image" === media_type ? imageUrl || "" : video?.url || "";
  const posterURL =
    "video" === media_type && video?.sizes?.large ? video.sizes.large : "";

  const handleSlideChange = (newIndex) => {
    if (newIndex === currentSlide || isFadingOut) return;
    setIsFadingOut(true);
    setTimeout(() => {
      setCurrentSlide(newIndex);
      setIsFadingOut(false);
    }, 250);
  };

  const handlePrevSlide = () => {
    if (isFadingOut) return;
    const prevIndex = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    handleSlideChange(prevIndex);
  };

  const handleNextSlide = () => {
    if (isFadingOut) return;
    const nextIndex = currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
    handleSlideChange(nextIndex);
  };

  const handleDotClick = (index) => {
    handleSlideChange(index);
  };

  // Maps custom positioning layouts to clean Tailwind strings
  let alignClass = "text-left items-start mx-0 mr-auto";
  if ("center" === align) {
    alignClass = "text-center items-center mx-auto";
  } else if ("right" === align) {
    alignClass = "text-right items-end mx-0 ml-auto";
  }

  const renderCTA = (_cta, _ctaColor) => {
    if (_cta) {
      const newAtts = getLinkUrl((_cta && _cta.url) || "");
      const colorClasses = tailwindColorOptions[_ctaColor || "transparent1"];

      return (
        <button
          onClick={() =>
            newAtts.external
              ? (window.location = newAtts.href)
              : navigate(newAtts.as)
          }
          className={`whitespace-nowrap px-6 text-lg py-3 border font-medium rounded transition-all duration-200 cursor-pointer max-w-none ${colorClasses}`}
        >
          {_cta.title}
        </button>
      );
    }
  };

  return (
    <div className="relative w-full">
      <Parallax
        strength={200}
        bgImage={imageUrl}
        bgImageAlt="Quam Speakers Slider Hero"
        bgClassName="object-cover !w-full"
        style={{ transition: "all 0.4s ease-in-out" }}
      >
        <div
          className={`flex flex-col px-16 md:px-0 relative md:w-248 mx-auto py-[15vw] min-h-80 h-auto overflow-hidden box-border transition-opacity duration-250 ease-in-out ${alignClass}`}
          style={{ opacity: isFadingOut ? 0 : 1 }}
        >
          {"video" === media_type && "mobile" !== windowSize.currDevice && (
            <VideoBackground
              url={mediaURL}
              poster={posterURL}
              autoplay={true}
              mute={true}
              background={true}
              loop={true}
              playsInline
            />
          )}

          {heading_richtext && (
            <div
              className={`relative w-full font-secondary [&_p]:font-secondary [&_p]:m-0 [&_p]:mb-4 text-5xl sm:text-[68px] md:text-header [&_p]:text-5xl [&_p]:sm:text-[68px] [&_p]:md:text-header font-regular [&_strong]:font-regular [&_p]:leading-[1.3] [&_p]:md:leading-header ${
                textColors[heading_color] || "text-white"
              } ${
                boldHeadingColors[bold_heading_rich_text_color] ||
                "[&_strong]:text-white"
              }`}
              dangerouslySetInnerHTML={{ __html: heading_richtext }}
            />
          )}

          {copy && (
            <p
              className={`relative w-full font-primary max-w-2/3 m-0 mb-6 text-base leading-6 md:text-lg lg:text-subhead lg:leading-[30px] ${
                bodyTextColors[copy_color] || "text-white"
              } ${
                align === "left"
                  ? "mr-auto ml-0"
                  : align === "right"
                    ? "ml-auto mr-0"
                    : "mx-auto"
              }`}
            >
              {copy}
            </p>
          )}

          <div className="flex flex-col gap-6 md:inline-flex md:flex-row">
            {renderCTA(cta, cta_color)}
            {renderCTA(cta_alt, cta_color_alt)}
          </div>

          {/* Navigation Dots */}
          {slides.length > 1 && (
            <div className="absolute bottom-icon-md left-1/2 -translate-x-1/2 flex gap-3 z-10">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-3 h-3 rounded-full border-2 border-white p-0 cursor-pointer outline-none transition-all duration-200 hover:bg-white/60 ${
                    index === currentSlide ? "bg-white" : "bg-transparent"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </Parallax>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrevSlide}
            aria-label="Previous slide"
            className="group absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 pr-0.5 rounded-full bg-black/35 hover:bg-black/60 active:scale-95 text-white backdrop-blur-sm border border-white/20 transition-all duration-200 cursor-pointer shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <svg
              className="w-3 h-5 md:w-3.5 md:h-6 transition-transform duration-200 group-hover:-translate-x-0.5"
              viewBox="-4 -4 56 93"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline
                fill="none"
                stroke="currentColor"
                strokeMiterlimit="10"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                points="44.92 81.89 5.31 42.27 44.92 2.65"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleNextSlide}
            aria-label="Next slide"
            className="group absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 pl-0.5 rounded-full bg-black/35 hover:bg-black/60 active:scale-95 text-white backdrop-blur-sm border border-white/20 transition-all duration-200 cursor-pointer shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <svg
              className="w-3 h-5 md:w-3.5 md:h-6 transition-transform duration-200 group-hover:translate-x-0.5"
              viewBox="-4 -4 56 93"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <polyline
                fill="none"
                stroke="currentColor"
                strokeMiterlimit="10"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                points="2.65 2.65 42.27 42.27 2.65 81.89"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
